import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, LogOut, Settings, CreditCard, ChevronDown } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

export default function NavigationHeader() {
  const { user, logout, isPremium } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate Gravatar URL
  const getGravatarUrl = (email: string, size: number = 40) => {
    
    // Create MD5 hash of email for Gravatar
    const emailHash = email.toLowerCase().trim();
    return `https://www.gravatar.com/avatar/${createHash(emailHash)}?s=${size}&d=mp&r=g`;
  };

  // Simple MD5-like hash function for demo purposes
  // In production, you'd want to use a proper crypto library
  const createHash = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  };

  const handleLogoClick = () => {
    navigate('/dashboard');
  };

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
  };

  const userDisplayName = user?.email || 'User';
  const userInitials = userDisplayName.slice(0, 2).toUpperCase();

  // Helper functions for subscription status display
  const formatRemainingTime = (endTimestamp: number) => {
    const now = Date.now();
    const endTime = endTimestamp * 1000; // Convert to milliseconds
    const timeLeft = endTime - now;
    
    if (timeLeft <= 0) {
      return 'Expired';
    }
    
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    
    if (days > 0) {
      return `${days}d`;
    } else {
      // For less than a day, show "1d" to avoid confusion
      return '1d';
    }
  };

  const getStatusBadge = () => {
    if (user?.isPremium) {
      if (user.membershipType === 'premium_prepaid' && user.prepaidUntil) {
        const remaining = formatRemainingTime(user.prepaidUntil);
        return {
          text: `Prepaid (${remaining})`,
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          dotColor: 'bg-green-600'
        };
      } else if (user.subscriptionCancelAtPeriodEnd && user.subscriptionCurrentPeriodEnd) {
        const remaining = formatRemainingTime(user.subscriptionCurrentPeriodEnd);
        return {
          text: `Ends in ${remaining}`,
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-800',
          dotColor: 'bg-orange-600'
        };
      }
      return {
        text: 'Premium',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        dotColor: 'bg-blue-600'
      };
    }
    return null; // No badge for free users
  };

  const getUserStatusText = () => {
    if (user?.isPremium) {
      if (user.membershipType === 'premium_prepaid' && user.prepaidUntil) {
        const remaining = formatRemainingTime(user.prepaidUntil);
        return `Prepaid Premium (${remaining} left)`;
      } else if (user.subscriptionCancelAtPeriodEnd && user.subscriptionCurrentPeriodEnd) {
        const remaining = formatRemainingTime(user.subscriptionCurrentPeriodEnd);
        return `Premium (${remaining} left)`;
      }
      return 'Premium User';
    }
    return 'Free User';
  };

  const statusBadge = getStatusBadge();
  const userStatusText = getUserStatusText();

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo/Brand - Clickable */}
          <button
            onClick={handleLogoClick}
            className="flex items-center hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-1"
          >
            <img src="/logo.svg" alt="Safermark Vaults" className="h-8 w-8 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Safermark</h1>
          </button>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Status Badge */}
            {statusBadge && (
              <div className={`hidden sm:flex items-center px-3 py-1 ${statusBadge.bgColor} ${statusBadge.textColor} text-sm font-medium rounded-full`}>
                <span className={`w-2 h-2 ${statusBadge.dotColor} rounded-full mr-2`}></span>
                {statusBadge.text}
              </div>
            )}

            {/* User Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-3 text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-2 transition-colors"
              >
                {/* User Avatar */}
                <div className="relative">
                  <img
                    src={getGravatarUrl(user?.email || '')}
                    alt={userDisplayName}
                    className="w-8 h-8 rounded-full object-cover ring-2 ring-gray-200"
                    onError={(e) => {
                      // Fallback to initials if Gravatar fails
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                  
                  {/* Fallback avatar with initials */}
                  <div className="hidden w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium ring-2 ring-gray-200">
                    {userInitials}
                  </div>
                  
                  {/* Online indicator */}
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
                </div>

                {/* User name and dropdown arrow */}
                <div className="hidden md:flex items-center space-x-2">
                  <div className="text-left">
                    <div className="text-sm font-medium text-gray-900">
                      {userDisplayName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {userStatusText}
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-transform ${
                    isDropdownOpen ? 'rotate-180' : ''
                  }`} />
                </div>

                {/* Mobile: Just show dropdown arrow */}
                <ChevronDown className={`w-4 h-4 md:hidden transition-transform ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`} />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <img
                            src={getGravatarUrl(user?.email || '', 32)}
                            alt={userDisplayName}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => {
                              // Fallback to initials if Gravatar fails
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                          <div className="hidden w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
                            {userInitials}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {userDisplayName}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {user?.email}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <Link
                      to="/profile"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <User className="w-4 h-4 mr-3" />
                      Profile Settings
                    </Link>

                    <Link
                      to="/subscription"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <CreditCard className="w-4 h-4 mr-3" />
                      {isPremium ? 'Manage Subscription' : 'Upgrade to Premium'}
                    </Link>

                    <Link
                      to="/vaults"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Settings className="w-4 h-4 mr-3" />
                      Manage Vaults
                    </Link>

                    {/* Divider */}
                    <div className="border-t border-gray-100 my-1"></div>

                    {/* Logout */}
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}